// @flow
import { arrToObject, updateArrayItem } from './array';
import { isEmpty } from './poly';
import { gather } from './function';
import { isDefined, isObject } from './type';

// Used because Object.values causes flow incompatibilities
// https://github.com/facebook/flow/issues/2221#issuecomment-372112477
export const values = <T>(obj: $ReadOnly<{ [string]: T }>): Array<T> =>
  Object.keys(obj).map(k => obj[k]);

// Consistent with `values` above
// https://github.com/facebook/flow/issues/2221#issuecomment-372112477
export const entries = <S, T>(obj: { [key: S]: T }): Array<[S, T]> =>
  Object.keys(obj).map(k => [k, obj[k]]);

/*
  Filter object `obj` by function `fn`. The function should be of arity 2
  and operate on (key, value). E.g.,
  filterObject({1: 2, 3: 4, 4: 10}, (k, v) => isOdd(k)) -> {1: 2, 3: 4}
*/
export const filterObject = <S, T>(
  obj: $ReadOnly<{ [S]: T }>,
  fn: (k: S, v: T) => boolean
) => arrToObject(Object.entries(obj).filter(gather(fn)));

/*
  Removes keys from `obj` whose values are null or undefined
*/
export const filterNulls = <S, T>(obj: $ReadOnly<{ [S]: ?T }>): { [S]: T } =>
  filterObject(obj, (_, v) => isDefined(v));

/*
  Map object `obj` by function `fn`. The function should be of arity 2
  and operate on (key, value), returning an array of length 2.
  mapObject({1: 2, 3: 4, 4: 10}, (k, v) => [k + 1, v + 1]) ->
  {2: 3, 4: 5, 5: 11}
*/
export const mapObject = <S, T, U>(
  obj: $ReadOnly<{ [S]: T }>,
  fn: (k: S, v: T) => [S, ?U]
) => arrToObject(Object.entries(obj).map(gather(fn)));

/*
  Same as mapObject, but the function operates on just the values.
  Passed function can take the key as the second argument if needed.
  mapValues({1: 2, 3: 4, 4: 10}, v => v + 1) -> {1: 3, 3: 5, 4: 11}
*/
export const mapValues = <S, T, U>(
  obj: $ReadOnly<{ [S]: T }>,
  fn: (v: T, k?: S) => ?U
) => mapObject(obj, (k, v) => [k, fn(v, k)]);

export const objectEmpty = (o: ?Object): boolean %checks =>
  !isObject(o) || isEmpty(Object.keys(o));

export const updateObject = (oldObject: Object, ...updates: Array<Object>) =>
  Object.assign({}, oldObject, ...updates);

export const removeKeys = (
  obj: $ReadOnly<{ [mixed]: mixed }>,
  keys: Array<mixed>
) => filterObject(obj, k => !keys.includes(k));

// Get an item in a nested object. E.g., getIn({a: {b: 2}}, ['a', 'b']) => 2
export const getIn = <T>(
  object: Object,
  accessor: $ReadOnlyArray<number | string> | string,
  defaultValue: ?T = null
): ?T => {
  const acc = accessor instanceof Array ? accessor : accessor.split('.');
  return isEmpty(acc)
    ? defaultValue
      ? object || defaultValue
      : object
    : object
    ? getIn(object[acc[0]], acc.slice(1), defaultValue)
    : defaultValue;
};

// Update an item in a nested object. E.g., updateIn({a: {b: 2}}, ['a', 'b'], x => x + 1) => {a: {b: 3}}
// If any levels do not exist, objects will be created
// Does not modify the original object
export const updateIn = <T>(
  object: Object,
  accessor: $ReadOnlyArray<string>,
  operation: T => T
) =>
  isEmpty(accessor)
    ? operation(object)
    : !object
    ? updateIn({}, accessor, operation)
    : Array.isArray(object)
    ? // handle 'objects' and 'arrays' with separate update operations
      updateArrayItem<Object>(
        object,
        Number(accessor[0]),
        updateIn(object[accessor[0]], accessor.slice(1), operation)
      )
    : updateObject(object, {
        [accessor[0]]: updateIn(
          object[accessor[0]],
          accessor.slice(1),
          operation
        )
      });

// Invert an object
// Note that values of object must be hashable (e.g., not mutable)
// If keys in original map are not strings, they are cast to strings as a result of
// Object.keys / Object.entries
export const invert = (object: Object) => mapObject(object, (k, v) => [v, k]);

export const getter = (attr: string) => (x: Object) => x[attr];

export const selectKeys = (object: Object, keys: Array<string>) =>
  filterObject(object, k => keys.includes(k));
